import type { CalcKey } from '../locales/keys';

export interface IntegralPreset {
  id: string;
  labelKey: CalcKey;
  /** Integrand f(x) as a parseable expression string. */
  f: string;
  /** Antiderivative F(x) as a parseable expression string; satisfies F′ = f. */
  F: string;
  /** Left endpoint of the integration interval. */
  a: number;
  /** Right endpoint of the integration interval. */
  b: number;
  /** Safe fitted x-domain when automatic context padding would cross a singularity. */
  fittedXDomain?: [number, number];
  /** Known x-values where sampled curve paths must be segmented. */
  discontinuities?: readonly number[];
}

export const INTEGRAL_PRESETS: IntegralPreset[] = [
  {
    id: 'polynomial',
    labelKey: 'preset.integral.polynomial',
    // F′ = x^2 ✓
    f: 'x^2',
    F: 'x^3 / 3',
    a: 0,
    b: 3,
  },
  {
    id: 'sine',
    labelKey: 'preset.integral.sine',
    // F′ = -(-sin(x)) = sin(x) ✓
    f: 'sin(x)',
    F: '-cos(x)',
    a: 0,
    b: Math.PI,
  },
  {
    id: 'cosine',
    labelKey: 'preset.integral.cosine',
    // F′ = cos(x) ✓
    f: 'cos(x)',
    F: 'sin(x)',
    a: -Math.PI / 2,
    b: Math.PI / 2,
  },
  {
    id: 'exponential',
    labelKey: 'preset.integral.exponential',
    // F′ = exp(x) ✓
    f: 'exp(x)',
    F: 'exp(x)',
    a: -1,
    b: 2,
  },
  {
    id: 'linear',
    labelKey: 'preset.integral.linear',
    // F′ = 2*x + 1 ✓; crosses zero on [-2, 3] → exercises signed area
    f: '2 * x + 1',
    F: 'x^2 + x',
    a: -2,
    b: 3,
  },
  {
    id: 'reciprocal',
    labelKey: 'preset.integral.reciprocal',
    // F′ = 1/x ✓; domain [0.5, 4] avoids the singularity at 0
    f: '1 / x',
    F: 'ln(x)',
    a: 0.5,
    b: 4,
    fittedXDomain: [0.5, 4],
    discontinuities: [0],
  },
];
